import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Download, Linkedin } from "lucide-react";
import { useI18n } from "../../i18n";
import { CV_CN_URL, CV_EN_URL, EASE, LINKEDIN_URL, lenisToggle } from "../../lib/site";
import { SealMark } from "./Logo";
import { Modal } from "./Modal";
import { About } from "./About";
import { Projects } from "./Projects";
import { Journey } from "./Journey";
import { Travel } from "./Travel";
import { Contact } from "./Contact";
import { SocialMedia } from "./SocialMedia";

const FolderIcon = ({ c }) => (
  <svg viewBox="0 0 72 56" className="h-12 w-[60px] drop-shadow-[0_10px_16px_rgba(90,62,43,0.30)] sm:h-14 sm:w-[70px] lg:h-16 lg:w-[82px]">
    <path d="M6 10 a4 4 0 0 1 4-4 h16 l6 6 h30 a4 4 0 0 1 4 4 v30 a4 4 0 0 1-4 4 H10 a4 4 0 0 1-4-4 z" fill={c} />
    <path d="M6 20 h60 v3 H6 z" fill="rgba(26,26,26,0.10)" />
  </svg>
);

// Desktop (lg+): 3 folders left of the photo, 3 right, mirrored stagger.
// Offsets live on a plain wrapper div so framer-motion's transform does not override them.
const ITEMS = [
  { id: "about", c: "#E9C9A6", side: "left", off: "lg:col-start-1 lg:row-start-1 lg:-translate-x-10" },
  { id: "projects", c: "#CBD3DD", side: "left", off: "lg:col-start-1 lg:row-start-2 lg:translate-x-10 lg:translate-y-4" },
  { id: "journey", c: "#E2B49F", side: "left", off: "lg:col-start-1 lg:row-start-3 lg:-translate-x-10 lg:translate-y-8" },
  { id: "travel", c: "#C9D1DB", side: "right", off: "lg:col-start-3 lg:row-start-1 lg:translate-x-10 lg:-translate-y-8" },
  { id: "contact", c: "#E7ACA4", side: "right", off: "lg:col-start-3 lg:row-start-2 lg:-translate-x-10 lg:-translate-y-4" },
  { id: "social", c: "#D9C6AE", side: "right", off: "lg:col-start-3 lg:row-start-3 lg:translate-x-10" },
];

export const Landing = () => {
  const { t } = useI18n();
  const [open, setOpen] = useState(null);
  const [cvOpen, setCvOpen] = useState(false);
  const cvRef = useRef(null);
  useEffect(() => { lenisToggle(!!open); }, [open]);
  useEffect(() => {
    if (!cvOpen) return;
    const onDoc = (e) => { if (cvRef.current && !cvRef.current.contains(e.target)) setCvOpen(false); };
    const onEsc = (e) => { if (e.key === "Escape") setCvOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onEsc); };
  }, [cvOpen]);

  const folder = (f, i) => (
    <div key={f.id} className={`flex justify-center ${f.off}`}>
      <motion.button
        onClick={() => setOpen(f.id)}
        data-testid={`folder-${f.id}`}
        initial={{ opacity: 0, scale: 0.7, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.7, ease: EASE, delay: 0.5 + i * 0.1 }}
        className="group flex w-[120px] flex-col items-center gap-2"
      >
        <motion.span
          animate={{ y: [0, -6, 0] }}
          transition={{ duration: 4.5 + i * 0.5, repeat: Infinity, ease: "easeInOut" }}
          className="transition-transform duration-300 ease-out group-hover:scale-110"
        >
          <FolderIcon c={f.c} />
        </motion.span>
        <span className="text-center text-xs font-semibold leading-tight text-[#5A3E2B] sm:text-sm">{t.landing.folders[f.id]}</span>
      </motion.button>
    </div>
  );

  return (
    <section data-testid="landing-section" className="relative flex min-h-[100dvh] flex-col overflow-x-hidden bg-[#F4EFE6] lg:h-[100dvh] lg:overflow-hidden">
      <header className="relative z-10 flex shrink-0 items-center justify-between px-5 py-4 sm:px-8 sm:py-5">
        <div className="flex items-center gap-3">
          <SealMark className="h-11 w-11 sm:h-12 sm:w-12" />
          <span data-testid="landing-title" className="whitespace-nowrap font-serif text-2xl text-ink sm:text-4xl">Ruiting Zou</span>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" data-testid="landing-linkedin-btn" className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#0A66C2] text-white shadow-[0_10px_24px_-10px_rgba(10,102,194,0.8)] transition-transform duration-300 hover:-translate-y-0.5 hover:bg-[#0A66C2]/90 sm:w-auto sm:gap-2 sm:px-5 sm:text-sm sm:font-semibold">
            <Linkedin className="h-4 w-4" /> <span className="hidden sm:inline">LinkedIn</span>
          </a>
          <div className="relative" ref={cvRef}>
            <button onClick={() => setCvOpen(!cvOpen)} aria-expanded={cvOpen} aria-haspopup="menu" data-testid="landing-cv-toggle-btn" className="inline-flex h-11 cursor-pointer items-center gap-1.5 rounded-full border border-ink/10 bg-white px-4 text-xs font-bold text-ink shadow-[0_10px_24px_-12px_rgba(90,62,43,0.45)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold hover:text-paper sm:px-5 sm:text-sm">
              <Download className="h-4 w-4" /> <span className="hidden sm:inline">{t.nav.cv}</span>
              <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${cvOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {cvOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -6, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -6, scale: 0.98 }}
                  transition={{ duration: 0.25, ease: EASE }}
                  data-testid="landing-cv-menu"
                  role="menu"
                  className="absolute right-0 top-[calc(100%+8px)] z-50 w-[300px] overflow-hidden rounded-2xl border border-ink/10 bg-white text-left shadow-[0_24px_50px_-20px_rgba(90,62,43,0.5)]"
                >
                  <a href={CV_EN_URL} download="Ruiting ZOU_Immediately Available_IANG Holder.pdf" title="English Resume for HK SAR & International opportunities" data-testid="landing-cv-english-option" role="menuitem" onClick={() => setCvOpen(false)} className="flex items-start gap-3 px-4 py-3 transition-colors hover:bg-alabaster">
                    <Download className="mt-0.5 h-4 w-4 shrink-0 text-terra" />
                    <span>
                      <span className="block text-sm font-bold text-ink">Download CV (Hong Kong SAR & Global)</span>
                      <span className="block text-[11px] leading-snug text-muted-ink">English Resume for HK SAR & International opportunities</span>
                    </span>
                  </a>
                  <a href={CV_CN_URL} download="邹瑞婷_立即到岗_中英文简历.pdf" title="Bilingual Resume (CN/EN) for Mainland China opportunities" data-testid="landing-cv-bilingual-option" role="menuitem" onClick={() => setCvOpen(false)} className="flex items-start gap-3 border-t border-ink/10 px-4 py-3 transition-colors hover:bg-alabaster">
                    <Download className="mt-0.5 h-4 w-4 shrink-0 text-terra" />
                    <span>
                      <span className="block text-sm font-bold text-ink">下载简历 (中国内地 / 双语版)</span>
                      <span className="block text-[11px] leading-snug text-muted-ink">Bilingual Resume (CN/EN) for Mainland China opportunities</span>
                    </span>
                  </a>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </header>

      {/* One grid, one set of folders.
          <lg : photo on top (spans all columns), folders below in 2 cols (3 cols on tablets).
          lg+ : [left folders | photo | right folders], photo spans the 3 rows. */}
      <div
        data-testid="landing-folders"
        className="mx-auto grid w-full max-w-[420px] flex-1 grid-cols-2 content-start justify-items-center gap-x-4 gap-y-7 px-4 pb-10 pt-2 md:max-w-[560px] md:grid-cols-3 lg:max-w-none lg:min-h-0 lg:grid-cols-[1fr_auto_1fr] lg:grid-rows-3 lg:content-center lg:items-center lg:gap-x-8 lg:gap-y-0 lg:px-8 lg:pb-6 lg:pt-0"
      >
        {/* Plain rectangular portrait: uncropped, original 1024x1366 ratio, no frame */}
        <motion.img
          src="/img/portrait.jpg"
          alt="Ruiting Zou holding her calligraphy bookmark"
          width={1024}
          height={1366}
          data-testid="landing-portrait"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="landing-portrait col-span-2 mb-1 block rounded-[4px] object-contain shadow-[0_18px_40px_-18px_rgba(90,62,43,0.45)] md:col-span-3 lg:col-span-1 lg:col-start-2 lg:row-span-3 lg:row-start-1 lg:mb-0"
        />
        {ITEMS.map((f, i) => folder(f, i))}
      </div>

      <Modal open={!!open} title={open ? t.landing.folders[open] : ""} onClose={() => setOpen(null)}>
        {open === "about" && <About />}
        {open === "projects" && <Projects />}
        {open === "journey" && <Journey />}
        {open === "travel" && <Travel />}
        {open === "social" && <SocialMedia />}
        {open === "contact" && <Contact />}
      </Modal>
    </section>
  );
};
